<script setup lang="ts">
/**
 * FaqAccordion — FAQ 手风琴（霓虹 chevron，展开旋转）
 */
import { ref } from 'vue'

defineProps<{
  items: { q: string; a: string }[]
}>()

const openIndex = ref<number | null>(null)

function toggle(idx: number) {
  openIndex.value = openIndex.value === idx ? null : idx
}
</script>

<template>
  <div class="faq">
    <div v-for="(item, idx) in items" :key="idx" class="faq__item" :class="{ 'faq__item--open': openIndex === idx }">
      <button class="faq__question" type="button" @click="toggle(idx)">
        <span class="faq__q-text">{{ item.q }}</span>
        <span class="faq__chevron" :class="{ 'faq__chevron--open': openIndex === idx }" aria-hidden="true">+</span>
      </button>
      <div v-if="openIndex === idx" class="faq__answer">
        <p>{{ item.a }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.faq {
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-3);
}

.faq__item {
  background-color: var(--yfb-surface);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-md);
  overflow: hidden;
  transition: border-color var(--yfb-anim-fast) ease-out, box-shadow var(--yfb-anim-fast) ease-out;
}

.faq__item:hover {
  border-color: var(--yfb-accent);
  box-shadow: 0 0 12px var(--yfb-glow);
}

.faq__question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--yfb-sp-4);
  width: 100%;
  padding: var(--yfb-sp-4) var(--yfb-sp-5);
  background-color: transparent;
  border: none;
  text-align: left;
  font-size: var(--yfb-fs-md);
  font-weight: 600;
  color: var(--yfb-ink);
  cursor: pointer;
  font-family: inherit;
}

.faq__question:hover {
  background-color: var(--yfb-surface-2);
}

.faq__chevron {
  font-size: var(--yfb-fs-2xl);
  font-weight: 700;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  border-radius: var(--yfb-radius-sm);
  transition: transform var(--yfb-anim-base) ease-out;
}

.faq__chevron--open {
  transform: rotate(45deg);
}

@media (prefers-reduced-motion: reduce) {
  .faq__chevron {
    transition: none;
  }
}

.faq__answer {
  padding: 0 20px 20px;
  border-top: 1px solid var(--yfb-rule-soft);
}

.faq__answer p {
  margin: var(--yfb-sp-4) 0 0;
  font-size: var(--yfb-fs-base);
  line-height: var(--yfb-lh-lead);
  color: var(--yfb-ink);
}
</style>